/**
 * API client for pipeline builder backend
 */

import axios from 'axios';
import { getApiKeyCookie, clearApiKeyCookie, getCbTokenCookie } from '../utils/authCookie';
import { ExecutionStatus } from '../types/pipeline';
import type { NodeExecution, PipelineDefinition, PipelineEdge, PipelineExecution, PipelineNode, StepInstanceExecution, TaskTypeInfo } from '../types/pipeline';
import type { ResolveAlmUrlResult, ResolveSourceItemsResult, Scope, WorkflowTriggerResult } from '../types/scope';
import type {
  AgentConfig,
  ModelProviderInfo,
  ModelInfo,
  ContextFieldTypeInfo,
  OutputFormatInfo,
} from '../types/agent';

// Exported so streaming callers that use fetch() (SSE, NDJSON) hit the API directly
// instead of the Vite proxy — the proxy passes /api/* through unrewritten and FastAPI
// has no /api prefix, so relative fetches would 404.
export const API_BASE_URL = import.meta.env.VITE_DNN_API_URL || 'http://dnn-api.dev-codebeamer.svc.cluster.local';

// Get the API key stored by the login gate (see LoginGate.tsx / authCookie.ts)
export const getApiKey = () => {
  const apiKey = getApiKeyCookie();
  if (!apiKey) {
    throw new Error('Not logged in. Please enter your API key.');
  }
  return apiKey;
};

// Check whether a candidate API key is valid, without touching the stored cookie or
// triggering the 403 interceptor below - used by the login gate to validate a key
// before it's ever stored.
export const validateApiKey = async (candidateKey: string): Promise<boolean> => {
  try {
    await axios.get(`${API_BASE_URL}/agents/output-formats`, {
      headers: { 'X-API-Key': candidateKey },
    });
    return true;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 403) {
      return false;
    }
    throw err;
  }
};

const axiosClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add API key to all requests
axiosClient.interceptors.request.use((config) => {
  config.headers['X-API-Key'] = getApiKey();
  return config;
});

// If the stored key is rejected, clear it and bounce back to the login gate.
axiosClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isAxiosError(error) && error.response?.status === 403) {
      clearApiKeyCookie();
      window.dispatchEvent(new Event('raiqon:auth-invalid'));
    }
    return Promise.reject(error);
  }
);

// Sanitize a frontend node id into a valid backend step name (must match ^[a-z][a-z0-9_]*$)
// Exported so execution-status consumers can map node.id -> step_name to look up
// backend-keyed node_executions entries (see PipelineCanvas.tsx, ExecutionMonitor.tsx).
export function sanitizeStepName(id: string): string {
  let name = id.toLowerCase().replace(/[^a-z0-9_]+/g, '_');
  if (!/^[a-z]/.test(name)) {
    name = 'step_' + name;
  }
  return name;
}

// Data source nodes are build-time declarations of what kind of data a workflow
// needs, not real backend TaskSteps - they map to WorkflowInput entries instead.
export const DATA_SOURCE_INPUT_TYPES: Record<string, string> = {
  data_source_item: 'item_id',
  data_source_tracker: 'tracker_id',
  data_source_project: 'project_id',
  data_source_chapter: 'chapter_id',
  data_source_tracker_schema: 'tracker_schema',
  data_source_outline: 'outline',
  data_source_tracker_list: 'tracker_list',
};

// Data sources that resolve to structured context rather than items - never drive execution
// and can only be connected as aggregate (mirrors ITEMLESS_INPUT_TYPES in raiqon/schemas/workflow.py).
export const ITEMLESS_DATA_SOURCE_TASK_TYPES = new Set(['data_source_tracker_schema', 'data_source_outline', 'data_source_tracker_list']);

export function isDataSourceNode(node: PipelineNode): boolean {
  return typeof node.task_type === 'string' && node.task_type in DATA_SOURCE_INPUT_TYPES;
}

// Converts an output node's value binding to the backend OutputValueBinding. Only the
// fields validate_source_consistency (workflow.py) allows for the source may be set -
// OutputValueBinding is a StrictModel (extra='forbid').
function toBackendValueBinding(valueBinding: any, dependencies: { step_name: string; required: boolean }[]): any {
  const backendValueBinding: any = { source: valueBinding.source };
  if (valueBinding.source === 'workflow_input') {
    backendValueBinding.source_name = valueBinding.source_name;
  } else if (valueBinding.source === 'step_output') {
    const upstreamStepName = sanitizeStepName(valueBinding.upstream_step_reference || '');
    backendValueBinding.upstream_step_reference = upstreamStepName;
    backendValueBinding.output_field = valueBinding.output_field;

    // Safety net: a step_output binding requires a required dependency on its
    // upstream step (enforced by validate_workflow_integrity), even if the
    // upstream-step picker chose a node other than a connected edge's source -
    // same pattern as the 'branch' condition safety net below.
    const existing = dependencies.find((d) => d.step_name === upstreamStepName);
    if (upstreamStepName && !existing) {
      dependencies.push({ step_name: upstreamStepName, required: true });
    } else if (existing) {
      existing.required = true;
    }
  } else if (valueBinding.source === 'item_field') {
    backendValueBinding.item_field = valueBinding.item_field;
  } else {
    backendValueBinding.literal_value = valueBinding.literal_value ?? '';
  }
  return backendValueBinding;
}

// Convert frontend pipeline format to backend workflow format
function pipelineToWorkflow(pipeline: PipelineDefinition): any {
  console.log('Converting pipeline to workflow:', pipeline);

  const dataSourceNodeIds = new Set(pipeline.nodes.filter(isDataSourceNode).map((node: PipelineNode) => node.id));
  const stepNodes = pipeline.nodes.filter((node: PipelineNode) => !dataSourceNodeIds.has(node.id));

  // Resolves a data-source node id back to the WorkflowInput name it declares,
  // so an edge leaving it can be recorded as a connected_inputs entry (see below)
  // instead of being silently dropped - this is the same name dataSourceInputs
  // uses, kept in sync so connected_inputs always references a real input.
  const dataSourceInputNameByNodeId = new Map(
    pipeline.nodes.filter(isDataSourceNode).map((node: PipelineNode) => [node.id, node.config?.name || sanitizeStepName(node.id)])
  );

  // Data source nodes never become steps - they declare a WorkflowInput instead,
  // so edges leaving them aren't real step dependencies and must be dropped here.
  const dataSourceInputs = pipeline.nodes.filter(isDataSourceNode).map((node: PipelineNode) => {
    const inputType = DATA_SOURCE_INPUT_TYPES[node.task_type];
    // An edge from a step into a data-source node means the input's value comes from
    // that (aggregate) step's output at run time - recorded as value_source.
    const feedingEdge = pipeline.edges.find(
      (edge: PipelineEdge) => edge.target === node.id && !dataSourceNodeIds.has(edge.source)
    );
    return {
      name: node.config?.name || sanitizeStepName(node.id),
      input_type: inputType,
      required: node.config?.required !== false,
      description: node.label || undefined,
      ...(inputType === 'outline' && node.config?.outline_depth ? { outline_depth: node.config.outline_depth } : {}),
      ...(feedingEdge
        ? {
            value_source: {
              upstream_step_reference: sanitizeStepName(feedingEdge.source),
              output_field: node.config?.value_source_field || 'response',
            },
          }
        : {}),
    };
  });

  // Convert remaining nodes to steps
  const steps = stepNodes.map((node: any) => {
    const stepName = sanitizeStepName(node.id);

    const dependencies = pipeline.edges
      .filter((edge: any) => edge.target === node.id && !dataSourceNodeIds.has(edge.source))
      .map((edge: any) => ({
        step_name: sanitizeStepName(edge.source),
        required: edge.type !== 'dashed',
      }));

    // Edges from a data-source node aren't dependencies (see above) - they're the step's
    // connected_inputs. How the step reads each input is decided by its agent's variables
    // (Single item / All items / Tracker schema), not by the edge.
    const connectedInputs = pipeline.edges
      .filter((edge: PipelineEdge) => edge.target === node.id && dataSourceNodeIds.has(edge.source))
      .map((edge: PipelineEdge) => ({ name: dataSourceInputNameByNodeId.get(edge.source) }));

    if (node.task_type === 'item_search' || node.task_type === 'reference_graph_query') {
      return {
        step_type: 'item_search',
        type_id: 'ItemSearchStepV1',
        name: stepName,
        description: node.label || node.id,
        search_kind: node.config?.search_kind || (node.task_type === 'reference_graph_query' ? 'reference_graph' : 'similarity'),
        max_results: node.config?.max_results ?? 5,
        score_threshold: node.config?.score_threshold,
        graph_direction: node.config?.graph_direction,
        max_depth: node.config?.max_depth,
        reference_types: node.config?.reference_types,
        dependencies,
        connected_inputs: connectedInputs,
        enabled: node.data?.enabled !== false,
      };
    }

    if (node.task_type === 'create_reference') {
      return {
        step_type: 'create_reference',
        type_id: 'CreateReferenceStepV1',
        name: stepName,
        description: node.label || node.id,
        reference_type: node.config?.reference_type || 'REFERENCE',
        field_id: node.config?.field_id || 'RQ_N/A',
        direction: node.config?.direction || 'item_to_related',
        cardinality: 'per_related_item',
        dependencies,
        connected_inputs: connectedInputs,
        enabled: node.data?.enabled !== false,
      };
    }

    if (node.task_type === 'pushback') {
      const valueBinding = node.config?.value_binding || { source: 'literal', literal_value: '' };
      const pushbackDependencies = [...dependencies];
      const loop = node.config?.loop;

      return {
        step_type: 'pushback',
        type_id: 'PushbackStepV1',
        name: stepName,
        description: node.label || node.id,
        ...(loop
          ? {
              loop: {
                ...loop,
                items_binding: toBackendValueBinding(loop.items_binding, pushbackDependencies),
              },
            }
          : {
              ...(node.config?.target_field_binding
                ? { target_field_binding: toBackendValueBinding(node.config.target_field_binding, pushbackDependencies) }
                : { target_field: node.config?.target_field || '' }),
              value_binding: toBackendValueBinding(valueBinding, pushbackDependencies),
            }),
        operation: node.config?.operation || 'OVERRIDE',
        target_scope_id: node.config?.target_scope_id || null,
        target_project_id: node.config?.target_project_id || null,
        target_type_id: node.config?.target_type_id || null,
        cardinality: 'per_item',
        dependencies: pushbackDependencies,
        connected_inputs: connectedInputs,
        enabled: node.data?.enabled !== false,
      };
    }

    if (node.task_type === 'create_item') {
      const createItemDependencies = [...dependencies];
      const cardinality = node.config?.cardinality || 'per_item';
      const fieldMappings = (node.config?.field_mappings || []).map((mapping: any) => ({
        target_field: mapping.target_field,
        value: toBackendValueBinding(mapping.value || { source: 'literal', literal_value: '' }, createItemDependencies),
        required: mapping.required === true,
      }));

      return {
        step_type: 'create_item',
        type_id: 'CreateItemStepV1',
        name: stepName,
        description: node.label || node.id,
        target_scope_id: node.config?.target_scope_id || '',
        target_project_id: node.config?.target_project_id || '',
        target_type_id: node.config?.target_type_id || '',
        field_mappings: fieldMappings,
        cardinality,
        // An aggregate run has no single source item to link back to.
        link_to_source: cardinality === 'per_item' && node.config?.link_to_source === true,
        link_reference_type: node.config?.link_reference_type || 'ITEM_CREATED',
        link_field_id: node.config?.link_field_id || 'RQ_N/A',
        dependencies: createItemDependencies,
        connected_inputs: connectedInputs,
        enabled: node.data?.enabled !== false,
      };
    }

    if (node.task_type === 'branch') {
      const condition = node.config?.condition;
      const upstreamStepName = condition ? sanitizeStepName(condition.upstream_step_reference) : undefined;

      // Safety net: the condition's upstream step must be a dependency, even if
      // the upstream-step picker chose a node other than the connected edge's source.
      if (upstreamStepName && !dependencies.some((d) => d.step_name === upstreamStepName)) {
        dependencies.push({ step_name: upstreamStepName, required: true });
      }

      // A branch feeding a data-source node isn't a branch target - only steps are.
      const outgoing = pipeline.edges.filter((edge: any) => edge.source === node.id && !dataSourceNodeIds.has(edge.target));
      const trueSteps = [...new Set(
        outgoing.filter((edge: any) => edge.source_handle === 'true').map((edge: any) => sanitizeStepName(edge.target))
      )];
      const falseSteps = [...new Set(
        outgoing.filter((edge: any) => edge.source_handle === 'false').map((edge: any) => sanitizeStepName(edge.target))
      )];

      return {
        step_type: 'conditional',
        type_id: 'ConditionalStepV1',
        name: stepName,
        description: node.label || node.id,
        condition: condition ? {
          upstream_step_reference: upstreamStepName,
          field: condition.field,
          operator: condition.operator,
          value: condition.value,
        } : undefined,
        true_steps: trueSteps,
        false_steps: falseSteps,
        dependencies,
        connected_inputs: connectedInputs,
        enabled: node.data?.enabled !== false,
      };
    }

    // cardinality lives alongside parameter bindings in node.config (see
    // StepCardinality in types/pipeline.ts) but is a sibling step field on the
    // backend, not a binding itself - strip it out before using the rest as
    // parameter_bindings as-is.
    const rawConfig = node.config || node.data?.config || {};
    const parameterBindings = { ...rawConfig };
    delete parameterBindings.cardinality;

    return {
      step_type: 'task',  // All other nodes are task steps
      type_id: 'TaskStepV1',
      name: stepName,
      description: node.label || node.id,
      agent: {
        agent_id: node.agent_id || null,
        agent_version: node.agent_version ?? null,
        task_type: node.task_type || node.data?.taskType,
      },
      cardinality: rawConfig.cardinality || 'per_item',
      parameter_bindings: parameterBindings,
      dependencies,
      connected_inputs: connectedInputs,
      enabled: node.data?.enabled !== false,
    };
  });

  // Use existing slug/version if available, otherwise generate from name
  let slug = (pipeline.metadata as any).slug;
  if (!slug) {
    slug = pipeline.metadata.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    // Ensure it starts with a letter
    if (!/^[a-z]/.test(slug)) {
      slug = 'workflow-' + slug;
    }
  }

  const version = (pipeline.metadata as any).version || 1;

  // Every input of a node-backed type (item_id/tracker_id/...) gets a data-source
  // node on load, so the canvas nodes are the sole source of truth for those -
  // only inputs of types with no node (e.g. content_hash, scope_id) are preserved
  // from metadata. Filtering by the node-backed types themselves (not by the types
  // currently on the canvas, nor by name) matters: deleting the last node of a type
  // or renaming a node would otherwise let its cached metadata.inputs entry survive
  // and reappear on the next load as an orphaned or duplicate node.
  const preservedInputs = ((pipeline.metadata as any).inputs || []).filter(
    (input: any) => !INPUT_TYPE_DATA_SOURCE_TASK_TYPES[input.input_type]
  );
  const inputs = [...preservedInputs, ...dataSourceInputs];

  return {
    id: pipeline.id,
    name: pipeline.metadata.name,
    slug,
    version,
    description: pipeline.metadata.description || '',
    inputs,  // Preserve workflow inputs so parameter bindings stay valid
    steps,
    tags: pipeline.metadata.tags || [],
    is_active: true,
  };
}

// Reverse of DATA_SOURCE_INPUT_TYPES - only inputs of these types get a
// synthesized data-source node; other input types (e.g. content_hash, scope_id)
// aren't backed by a palette entry yet and are left as inputs-only.
const INPUT_TYPE_DATA_SOURCE_TASK_TYPES: Record<string, string> = {
  item_id: 'data_source_item',
  tracker_id: 'data_source_tracker',
  project_id: 'data_source_project',
  chapter_id: 'data_source_chapter',
  tracker_schema: 'data_source_tracker_schema',
  outline: 'data_source_outline',
  tracker_list: 'data_source_tracker_list',
};

// Convert backend workflow format to frontend pipeline format
function workflowToPipeline(workflow: any): PipelineDefinition {
  console.log('Converting workflow to pipeline:', workflow);
  console.log('Workflow steps:', workflow.steps);

  // Reconstruct one data-source node per declared input so build-time type
  // declarations remain visible/editable in the canvas after a reload.
  const dataSourceNodes = (workflow.inputs || [])
    .filter((input: any) => INPUT_TYPE_DATA_SOURCE_TASK_TYPES[input.input_type])
    .map((input: any, index: number) => {
      const taskType = INPUT_TYPE_DATA_SOURCE_TASK_TYPES[input.input_type];
      const config = {
        name: input.name,
        required: input.required !== false,
        ...(input.outline_depth ? { outline_depth: input.outline_depth } : {}),
        ...(input.value_source ? { value_source_field: input.value_source.output_field || 'response' } : {}),
      };
      return {
        id: `data_source_${input.name}`,
        type: 'taskNode',  // Must match nodeTypes registration in PipelineCanvas
        position: { x: 100 + (index * 250), y: -150 },  // Above the step row
        task_type: taskType,
        label: input.name,
        config,
        data: {
          taskType,
          label: input.name,
          description: input.description || '',
          config,
          enabled: true,
        },
      };
    });

  const stepNodes = (workflow.steps || []).map((step: any, index: number) => {
    const position = { x: 100 + (index * 250), y: 100 };  // Auto-layout horizontally

    if (step.step_type === 'conditional') {
      return {
        id: step.name,
        type: 'conditionalNode',  // Must match nodeTypes registration in PipelineCanvas
        position,
        task_type: 'branch',
        label: step.description || step.name,
        config: { condition: step.condition },
        data: {
          taskType: 'branch',
          label: step.description || step.name,
          condition: step.condition,
          enabled: step.enabled !== false,
        },
      };
    }

    if (step.step_type === 'item_search') {
      const isReferenceGraph = step.search_kind === 'reference_graph';
      const taskType = isReferenceGraph ? 'reference_graph_query' : 'item_search';
      const config = isReferenceGraph
        ? {
            search_kind: step.search_kind,
            max_results: step.max_results ?? 5,
            graph_direction: step.graph_direction,
            max_depth: step.max_depth,
            reference_types: step.reference_types,
          }
        : {
            search_kind: step.search_kind || 'similarity',
            max_results: step.max_results ?? 5,
            score_threshold: step.score_threshold,
          };
      return {
        id: step.name,
        type: 'taskNode',  // Must match nodeTypes registration in PipelineCanvas
        position,
        task_type: taskType,
        label: step.description || step.name,
        config,
        data: {
          taskType,
          label: step.description || step.name,
          description: step.description || '',
          config,
          enabled: step.enabled !== false,
        },
      };
    }

    if (step.step_type === 'create_reference') {
      const config = {
        reference_type: step.reference_type || 'REFERENCE',
        field_id: step.field_id || 'RQ_N/A',
        direction: step.direction || 'item_to_related',
        cardinality: 'per_related_item',
      };
      return {
        id: step.name,
        type: 'taskNode',  // Must match nodeTypes registration in PipelineCanvas
        position,
        task_type: 'create_reference',
        label: step.description || step.name,
        config,
        data: {
          taskType: 'create_reference',
          label: step.description || step.name,
          description: step.description || '',
          config,
          enabled: step.enabled !== false,
        },
      };
    }

    if (step.step_type === 'create_item') {
      const config = {
        target_scope_id: step.target_scope_id || '',
        target_project_id: step.target_project_id || '',
        target_type_id: step.target_type_id || '',
        // upstream_step_reference is already the upstream step's name, i.e. the node id
        // workflowToPipeline gives that step - same round-trip as pushback below.
        field_mappings: (step.field_mappings || []).map((mapping: any) => ({
          target_field: mapping.target_field,
          value: { ...mapping.value },
          required: mapping.required === true,
        })),
        cardinality: step.cardinality || 'per_item',
        link_to_source: step.link_to_source === true,
        link_reference_type: step.link_reference_type || 'ITEM_CREATED',
        link_field_id: step.link_field_id || 'RQ_N/A',
      };
      return {
        id: step.name,
        type: 'taskNode',  // Must match nodeTypes registration in PipelineCanvas
        position,
        task_type: 'create_item',
        label: step.description || step.name,
        config,
        data: {
          taskType: 'create_item',
          label: step.description || step.name,
          description: step.description || '',
          config,
          enabled: step.enabled !== false,
        },
      };
    }

    if (step.step_type === 'pushback') {
      const valueBinding = step.value_binding || { source: 'literal' };
      const config = step.loop
        ? {
            // items_binding.upstream_step_reference is the upstream step's name, which is the
            // id workflowToPipeline gives that step's node - same round-trip as value_binding.
            loop: {
              items_binding: { ...step.loop.items_binding },
              target_field: { ...step.loop.target_field },
              value: { ...step.loop.value },
              ...(step.loop.operation && { operation: { ...step.loop.operation } }),
            },
            operation: step.operation || 'OVERRIDE',
            target_scope_id: step.target_scope_id || '',
            target_project_id: step.target_project_id || '',
            target_type_id: step.target_type_id || '',
          }
        : {
        ...(step.target_field_binding
          ? { target_field_binding: { ...step.target_field_binding } }
          : { target_field: step.target_field || '' }),
        operation: step.operation || 'OVERRIDE',
        target_scope_id: step.target_scope_id || '',
        target_project_id: step.target_project_id || '',
        target_type_id: step.target_type_id || '',
        value_binding: {
          source: valueBinding.source,
          literal_value: valueBinding.literal_value,
          source_name: valueBinding.source_name,
          // upstream_step_reference is already the upstream step's name here, which
          // is exactly the id workflowToPipeline gives that step's own node below -
          // so it round-trips into PushbackConfig's upstream-step picker unchanged.
          upstream_step_reference: valueBinding.upstream_step_reference,
          output_field: valueBinding.output_field,
          item_field: valueBinding.item_field,
        },
      };
      return {
        id: step.name,
        type: 'taskNode',  // Must match nodeTypes registration in PipelineCanvas
        position,
        task_type: 'pushback',
        label: step.description || step.name,
        config,
        data: {
          taskType: 'pushback',
          label: step.description || step.name,
          description: step.description || '',
          config,
          enabled: step.enabled !== false,
        },
      };
    }

    // cardinality is a sibling step field on the backend but rides alongside
    // parameter bindings in node.config on the frontend (see pipelineToWorkflow).
    const config = {
      ...(step.parameter_bindings || {}),
      cardinality: step.cardinality || 'per_item',
    };

    return {
      id: step.name,
      type: 'taskNode',  // Must match nodeTypes registration in PipelineCanvas
      position,
      // PipelineCanvas expects these flat properties for syncing
      task_type: step.agent?.task_type || '',
      label: step.description || step.name,
      config,
      agent_id: step.agent?.agent_id || '',
      agent_version: step.agent?.agent_version ?? undefined,
      // React Flow expects data object for custom component props
      data: {
        taskType: step.agent?.task_type || '',
        label: step.description || step.name,
        description: step.description || '',
        config,
        agentId: step.agent?.agent_id || '',
        agentVersion: step.agent?.agent_version ?? undefined,
        agentName: step.agent?.task_type || '',  // Use task_type as name for now
        enabled: step.enabled !== false,
      }
    };
  });

  const nodes = [...dataSourceNodes, ...stepNodes];

  const stepsByName = new Map<string, any>((workflow.steps || []).map((s: any) => [s.name, s]));

  const edges = (workflow.steps || []).flatMap((step: any) =>
    (step.dependencies || []).map((dep: any) => {
      // Reconstruct which named handle this edge originates from when its
      // source is a branch node - otherwise true/false wiring collapses to
      // a single handle on reload.
      const sourceStep = stepsByName.get(dep.step_name);
      let sourceHandle: 'true' | 'false' | undefined;
      if (sourceStep?.step_type === 'conditional') {
        if (sourceStep.true_steps?.includes(step.name)) sourceHandle = 'true';
        else if (sourceStep.false_steps?.includes(step.name)) sourceHandle = 'false';
      }

      return {
        id: `${dep.step_name}-${step.name}`,
        source: dep.step_name,
        target: step.name,
        type: dep.required ? 'default' : 'dashed',
        source_handle: sourceHandle,
      };
    })
  );

  // Reconstruct edges from data-source nodes using the same deterministic
  // `data_source_${name}` id the dataSourceNodes above were just built with -
  // these aren't in step.dependencies (see pipelineToWorkflow) so they need
  // their own pass, sourced from connected_inputs instead.
  const dataSourceInputEdges = (workflow.steps || []).flatMap((step: any) =>
    (step.connected_inputs || []).map((connection: { name: string }) => ({
      id: `data_source_${connection.name}-${step.name}`,
      source: `data_source_${connection.name}`,
      target: step.name,
      type: 'default',
    }))
  );

  // Reconstruct step -> data-source edges for inputs filled in from an upstream
  // step's output (value_source) - the step node's id is its step name.
  const valueSourceEdges = (workflow.inputs || [])
    .filter((input: any) => input.value_source && INPUT_TYPE_DATA_SOURCE_TASK_TYPES[input.input_type])
    .map((input: any) => ({
      id: `${input.value_source.upstream_step_reference}-data_source_${input.name}`,
      source: input.value_source.upstream_step_reference,
      target: `data_source_${input.name}`,
      type: 'default',
    }));

  const allEdges = [...edges, ...dataSourceInputEdges, ...valueSourceEdges];

  console.log('Generated nodes:', nodes);
  console.log('Generated edges:', allEdges);

  return {
    id: workflow.id,
    metadata: {
      name: workflow.name,
      description: workflow.description || '',
      tags: workflow.tags || [],
      created_at: workflow.created_at || new Date().toISOString(),
      updated_at: workflow.updated_at || new Date().toISOString(),
      created_by: 'system',  // TODO: get from workflow owner
      // Store backend-specific fields so we can send them back on update
      slug: workflow.slug,
      version: workflow.version,
      inputs: workflow.inputs || [],  // Preserve workflow inputs for parameter bindings
    },
    nodes,
    edges: allEdges,
  };
}

/**
 * Derive a URL/slug-safe identifier from an agent's display name. Shared by
 * toCreateAgentRequest (fallback when no explicit slug is set) and the agent
 * import flow (deduping an imported agent's slug against existing ones).
 */
export function slugifyAgentName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

/**
 * Shared `config` (prompt_config/inference_params/context_config/output_config)
 * plus model_slug, built once and reused by both the create and update payloads
 * below.
 */
function toAgentConfigFields(config: Partial<AgentConfig>): {
  model_slug: string | null;
  config: Record<string, any>;
} {
  const model = config.model;
  const prompt = config.prompt;
  const output = config.output;

  // Backend GET populates model.model_slug from the resolved model row
  // (see get_agent in agents_utils.py). The backend looks models up by
  // slug, not by id, so this must round-trip as model_slug.
  const model_slug = model?.model_slug ?? null;

  const inference_params: Record<string, any> = {};
  if (model?.temperature !== undefined) inference_params.temperature = model.temperature;
  if (model?.max_tokens !== undefined) inference_params.max_tokens = model.max_tokens;
  if (model?.top_p !== undefined) inference_params.top_p = model.top_p;
  if (model?.stop_sequences?.length) inference_params.stop_sequences = model.stop_sequences;
  if (model?.reasoning_enabled !== undefined) inference_params.reasoning_enabled = model.reasoning_enabled;

  const prompt_config: Record<string, any> = {};
  if (prompt?.system_prompt !== undefined) prompt_config.system_prompt = prompt.system_prompt;
  // Backend GET maps prompt_config.user_prompt -> user_prompt_template; reverse that here.
  if (prompt?.user_prompt_template !== undefined) prompt_config.user_prompt = prompt.user_prompt_template;
  if (prompt?.variables !== undefined) prompt_config.variables = prompt.variables;

  const context_config: Record<string, any> = {
    fields: config.context_fields ?? [],
  };

  const output_config: Record<string, any> = {};
  if (output?.format !== undefined) output_config.format = output.format;
  if (output?.output_schema !== undefined) output_config.schema = output.output_schema;
  if (output?.validation_rules !== undefined) output_config.validation_rules = output.validation_rules;

  return {
    model_slug,
    config: { prompt_config, inference_params, context_config, output_config },
  };
}

/**
 * Convert the UI-side AgentConfig into the CreateAgentRequest shape the
 * backend POST /agents endpoint requires (agents.py). That model is a
 * StrictModel (extra="forbid"), so it must NOT include `id` (the backend
 * generates it deterministically) or anything else it doesn't declare -
 * unlike a plain BaseModel, an unexpected field here is a 422, not a no-op.
 */
function toCreateAgentRequest(config: Partial<AgentConfig>): Record<string, any> {
  // CreateAgentRequest.version is int; UI carries "1.0.0" style strings. Take
  // the leading integer, fall back to 1.
  let versionInt = 1;
  if (typeof config.version === 'string') {
    const parsed = parseInt(config.version.split('.')[0], 10);
    if (!isNaN(parsed) && parsed > 0) versionInt = parsed;
  } else if (typeof config.version === 'number') {
    versionInt = config.version;
  }

  const { model_slug, config: agentConfig } = toAgentConfigFields(config);

  return {
    name: config.name ?? '',
    slug: config.slug ?? slugifyAgentName(config.name ?? ''),
    version: versionInt,
    task_type: config.task_type ?? '',
    description: config.description ?? null,
    model_slug,
    config: agentConfig,
    category: config.category ?? 'other',
    tags: config.tags ?? [],
    is_default: config.is_default ?? false,
    is_active: true,
  };
}

/**
 * Convert the UI-side AgentConfig into the UpdateAgentRequest shape the
 * backend PUT /agents/{id} endpoint requires (agents.py). Unlike create,
 * this StrictModel has no id/slug/version/task_type/is_default fields at
 * all - update_agent() creates a new (slug, version) row from the existing
 * one server-side, so those must be omitted, not just left unchanged.
 */
function toUpdateAgentRequest(config: Partial<AgentConfig>): Record<string, any> {
  const { model_slug, config: agentConfig } = toAgentConfigFields(config);

  return {
    name: config.name ?? null,
    description: config.description ?? null,
    model_slug,
    config: agentConfig,
    category: config.category ?? null,
    tags: config.tags ?? null,
    is_active: true,
  };
}

// The metadata fields a save derives from the pipeline (inputs from data-source nodes, slug
// from the name) - written back into the editor's pipeline so it matches what a reload would
// give, e.g. the execution wizard sees the just-saved inputs.
export type SavedWorkflowFields = { inputs: any[]; slug: string; version: number };

function savedWorkflowFields(workflowDef: any): SavedWorkflowFields {
  return { inputs: workflowDef.inputs, slug: workflowDef.slug, version: workflowDef.version };
}

const EXECUTION_STATUS_MAP: Record<string, ExecutionStatus> = {
  pending: ExecutionStatus.PENDING,
  running: ExecutionStatus.RUNNING,
  completed: ExecutionStatus.COMPLETED,
  failed: ExecutionStatus.FAILED,
  not_found: ExecutionStatus.FAILED,
  unknown: ExecutionStatus.FAILED,
};

function toStepInstanceExecution(instance: any): StepInstanceExecution {
  return {
    item_key: instance.item_key,
    task_id: instance.task_id ?? null,
    status: EXECUTION_STATUS_MAP[instance.status] ?? ExecutionStatus.FAILED,
    rendered_system_prompt: instance.rendered_system_prompt,
    rendered_user_prompt: instance.rendered_user_prompt,
    inputs: instance.inputs,
    result: instance.result,
    error: instance.error,
    started_at: instance.started_at,
    completed_at: instance.completed_at,
  };
}

export const pipelineApi = {
  // Create workflow (renamed from pipeline)
  async createPipeline(pipeline: PipelineDefinition): Promise<{ pipeline_id: string; saved: SavedWorkflowFields }> {
    // Convert frontend PipelineDefinition to backend WorkflowDefinition format
    const workflowDef = pipelineToWorkflow(pipeline);
    console.log('Sending workflow create:', JSON.stringify(workflowDef, null, 2));
    try {
      const response = await axiosClient.post('/workflows/create', workflowDef);
      return { pipeline_id: response.data.id, saved: savedWorkflowFields(workflowDef) };
    } catch (err: any) {
      console.error('Create failed. Full error:', JSON.stringify(err.response?.data, null, 2));
      console.error('Status:', err.response?.status);
      throw err;
    }
  },

  // List workflows
  async listPipelines(): Promise<PipelineDefinition[]> {
    const response = await axiosClient.get('/workflows');
    const workflows = response.data.workflows || [];

    // Transform backend format to frontend format
    return workflows.map((w: any) => ({
      id: w.id,
      metadata: {
        name: w.name,
        description: w.description || '',
        tags: w.tags || [],
        // Straight from the DB (updated_at = last save). Empty when unknown - never "now", which
        // would make every workflow look freshly saved and break sorting by date.
        created_at: w.created_at || '',
        updated_at: w.updated_at || '',
        created_by: 'system',  // TODO: get from workflow owner
      },
      nodes: [],
      edges: [],
      node_count: w.node_count,
      edge_count: w.edge_count,
    }));
  },

  // Get workflow
  async getPipeline(pipelineId: string): Promise<PipelineDefinition> {
    const response = await axiosClient.get(`/workflows/${pipelineId}`);
    return workflowToPipeline(response.data);
  },

  // Update workflow
  async updatePipeline(pipelineId: string, pipeline: PipelineDefinition): Promise<SavedWorkflowFields> {
    // Convert frontend PipelineDefinition to backend WorkflowDefinition format
    const workflowDef = pipelineToWorkflow(pipeline);
    console.log('Sending workflow update:', JSON.stringify(workflowDef, null, 2));
    try {
      await axiosClient.put(`/workflows/${pipelineId}`, workflowDef);
      return savedWorkflowFields(workflowDef);
    } catch (err: any) {
      console.error('Update failed. Full error:', JSON.stringify(err.response?.data, null, 2));
      console.error('Status:', err.response?.status);
      throw err;
    }
  },

  // Names of the inputs the saved workflow iterates over - derived by the backend from how each
  // step's agent reads its connected inputs (Single item vs All items / Tracker schema).
  async getDrivingInputs(pipelineId: string): Promise<string[]> {
    const response = await axiosClient.get(`/workflows/${pipelineId}/driving-inputs`);
    return response.data;
  },

  // Delete workflow
  async deletePipeline(pipelineId: string): Promise<void> {
    await axiosClient.delete(`/workflows/${pipelineId}`);
  },

  // Delete a workflow execution (Postgres rows + any still-in-flight queue tasks)
  async deleteExecution(executionId: string): Promise<void> {
    await axiosClient.delete(`/workflows/executions/${executionId}`);
  },

  // Trigger a workflow once, given one Scope per declared WorkflowInput name. Inputs a step
  // reads as a single item are unioned into the batch that fans out internally; inputs read
  // only as all items / schema stay addressable via scope.<name>.<field>. One result per call - poll its
  // completion_barrier_id to check overall completion. cbToken is the user's Codebeamer JWT,
  // forwarded to the CB Connector bridge for tracker_schema inputs. Falls back to the token
  // saved in Settings when none is passed explicitly.
  async executePipeline(
    pipelineId: string,
    namedInputs: Record<string, Scope>,
    cbToken?: string,
  ): Promise<WorkflowTriggerResult> {
    cbToken = cbToken || getCbTokenCookie() || undefined;
    const response = await axiosClient.post(
      '/workflows/trigger',
      { workflow_id: pipelineId, named_inputs: namedInputs },
      cbToken ? { headers: { Authorization: `Bearer ${cbToken}` } } : undefined,
    );
    return response.data;
  },

  // Resolve source_item ids (e.g. Codebeamer issue ids) to internal item UUIDs.
  async resolveSourceItems(sourceItems: string[]): Promise<ResolveSourceItemsResult> {
    const response = await axiosClient.post('/workflows/resolve-source-items', { source_items: sourceItems });
    return response.data;
  },

  // Resolve a pasted ALM (e.g. Codebeamer) issue/tracker/project URL to the Scope it names.
  // With inputType, a bare external id is accepted too, and a chapter_id input resolves its
  // issue URL to a ScopeFromChapter.
  async resolveAlmUrl(url: string, inputType?: string): Promise<ResolveAlmUrlResult> {
    const response = await axiosClient.post('/workflows/resolve-alm-url', {
      url,
      ...(inputType ? { input_type: inputType } : {}),
    });
    return response.data;
  },

  // Suggested field names for items of a target tracker (Create Item field mapping).
  async getTargetFields(scopeId: string, typeId: string): Promise<string[]> {
    const response = await axiosClient.get('/workflows/target-fields', {
      params: { scope_id: scopeId, type_id: typeId },
    });
    return response.data;
  },

  // Source item field names an output node's item_field binding can read, from the
  // context-options item fields ("item.X" paths) with the "item." prefix stripped.
  async getOutputItemFieldNames(): Promise<string[]> {
    const response = await axiosClient.get('/workflows/context-options');
    const fields: string[] = (response.data.item_fields || [])
      .map((f: any) => String(f.path || '').replace(/^item\./, ''))
      .filter((f: string) => f && f !== 'id');
    return [...new Set(fields)];
  },

  // Get execution status and per-node detail in one poll. With includeDetails=false the
  // instances carry statuses/errors/timings only (no prompts/inputs/results) - fetch a single
  // instance's payload lazily via getStepInstanceDetail().
  async getExecutionStatus(executionId: string, includeDetails = true): Promise<PipelineExecution> {
    const response = await axiosClient.get(`/workflows/executions/${executionId}/status`, {
      params: includeDetails ? undefined : { include_details: false },
    });
    const data = response.data;

    const status = EXECUTION_STATUS_MAP[data.status] ?? ExecutionStatus.FAILED;
    const isTerminal = status === ExecutionStatus.COMPLETED || status === ExecutionStatus.FAILED;

    const nodeExecutions: Record<string, NodeExecution> = {};
    for (const [nodeId, node] of Object.entries<any>(data.nodes ?? {})) {
      const instances: StepInstanceExecution[] = (node.instances ?? []).map(toStepInstanceExecution);
      // Legacy single-value fields for the (unchanged) List tab: derived from the
      // representative instance - the only one for aggregate steps, the first for others.
      const primary = instances[0];
      nodeExecutions[nodeId] = {
        node_id: nodeId,
        status: EXECUTION_STATUS_MAP[node.status] ?? ExecutionStatus.FAILED,
        instances,
        started_at: primary?.started_at ?? undefined,
        completed_at: primary?.completed_at ?? undefined,
        result: primary?.result ?? undefined,
        error: primary?.error ?? undefined,
        task_id: primary?.task_id ?? undefined,
      };
    }

    return {
      id: data.execution_id,
      // workflow_id === the pipeline's own id: executePipeline() posts workflow_id: pipelineId,
      // so this is the same UUID, just returned back from the trigger/status round-trip.
      pipeline_id: data.workflow_id || '',
      status,
      started_at: data.created_at ?? new Date().toISOString(),
      completed_at: isTerminal ? (data.completed_at ?? undefined) : undefined,
      item_ids: data.item_ids ?? null,
      node_executions: nodeExecutions,
    };
  },

  // Full prompts/inputs/result for one step instance - loaded on demand when the user opens it.
  async getStepInstanceDetail(executionId: string, taskId: string): Promise<StepInstanceExecution> {
    const response = await axiosClient.get(`/workflows/executions/${executionId}/steps/${taskId}`);
    return toStepInstanceExecution(response.data);
  },

  // List recent executions across all workflows, then fetch per-node statuses (no payloads) for each.
  // One page of executions (most recent first) plus the total count for pagination.
  async listExecutions(
    limit = 50,
    offset = 0,
    workflowId?: string,
  ): Promise<{ executions: PipelineExecution[]; total: number }> {
    const response = await axiosClient.get('/workflows/executions', {
      params: { limit, offset, workflow_id: workflowId },
    });
    const summaries: { execution_id: string }[] = response.data.executions || [];

    const details = await Promise.all(
      summaries.map(async (summary) => {
        try {
          return await this.getExecutionStatus(summary.execution_id, false);
        } catch (err) {
          console.error(`Failed to load execution status for ${summary.execution_id}:`, err);
          return null;
        }
      })
    );

    return {
      executions: details.filter((detail): detail is PipelineExecution => detail !== null),
      total: response.data.total ?? summaries.length,
    };
  },

  // Get available task types
  async getTaskTypes(): Promise<TaskTypeInfo[]> {
    const response = await axiosClient.get('/workflows/context-options');

    // Group task types into logical categories
    const getCategory = (taskType: string): string => {
      if (taskType.includes('analysis') || taskType.includes('review') || taskType.includes('check')) return 'analysis';
      if (taskType.includes('authoring') || taskType.includes('improvement') || taskType.includes('generation')) return 'authoring';
      if (taskType.includes('chat')) return 'chat';
      if (taskType.includes('decomposition') || taskType.includes('extraction') || taskType.includes('grouping')) return 'decomposition';
      if (taskType.includes('test') || taskType.includes('condition') || taskType.includes('case')) return 'testing';
      return 'other';
    };

    return response.data.task_types.map((t: any) => ({
      id: t.value,
      type: t.value,
      label: t.label,
      description: t.value === 'agent'
        ? 'Pick any agent for this node, including your own custom agents.'
        : `Runs with the default agent for ${t.label}. To use one of your own custom agents, drag in an Agent node instead.`,
      category: getCategory(t.value)
    }));
  },

  // TODO: SSE stream for execution monitoring
  getStreamUrl(executionId: string): string {
    return `${API_BASE_URL}/workflows/stream/${executionId}?api_key=${getApiKey()}`;
  },

  // TODO: Data source endpoints - not yet implemented
  async getFieldsToSync(): Promise<string[]> {
    console.warn('getFieldsToSync not implemented');
    return [];
  },

  async getItemFields(): Promise<Array<{name: string; type: string; description: string; is_system: boolean}>> {
    console.warn('getItemFields not implemented');
    return [];
  },

  async getTrackerAttributes(): Promise<Array<{name: string; type: string; description: string}>> {
    console.warn('getTrackerAttributes not implemented');
    return [];
  },

  async getProjectAttributes(): Promise<Array<{name: string; type: string; description: string}>> {
    console.warn('getProjectAttributes not implemented');
    return [];
  },

  async validateUuid(uuid: string): Promise<boolean> {
    // Simple client-side validation
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  },

  async getTrackerItems(trackerId: string): Promise<{item_ids: string[]; count: number; tracker_id: string}> {
    console.warn('getTrackerItems not implemented');
    return { item_ids: [], count: 0, tracker_id: trackerId };
  },

  async getItemMetadata(externalId: number, properties: string[]): Promise<{item: any; external_id: number; properties: string[]}> {
    console.warn('getItemMetadata not implemented');
    return { item: {}, external_id: externalId, properties };
  },
};

export const agentApi = {
  // List agents
  async listAgents(category?: string): Promise<AgentConfig[]> {
    const params: any = {};
    if (category) params.task_type = category; // Backend uses task_type not category
    const response = await axiosClient.get('/agents', { params });

    // Backend returns AgentSummary - map to AgentConfig structure for UI
    return (response.data || []).map((agent: any) => ({
      id: agent.id,
      name: agent.name,
      slug: agent.slug,
      version: agent.version || '1.0.0',
      task_type: agent.task_type,
      description: agent.description || '',
      category: agent.category || 'other',
      tags: agent.tags || [],
      // From the DB; updated_at is this version's last save (empty when unknown, never "now").
      created_at: agent.created_at || '',
      updated_at: agent.updated_at || '',
      created_by: agent.created_by || '00000000-0000-0000-0000-000000000000',
      is_default: agent.is_default || false,
      is_public: agent.is_public || false,
      usage_count: agent.usage_count || 0,
      timeout: 300,
      retry_count: 3,
      // Model from summary
      model: {
        model_id: '',
        model_name: agent.model_name || 'Unknown',
        model_slug: '',
        provider: agent.provider || 'unknown',
      },
      // Placeholder structures - need full fetch to get these
      prompt: { system_prompt: '', user_prompt: '', variables: [] },
      context_fields: [],
      // Computed client-side from the raw context_config the summary now carries,
      // rather than a backend-precomputed count.
      context_field_count: (agent.context_config?.fields || []).length,
      output: { format: 'text', validation_rules: [] },
    }));
  },

  // Get agents grouped by category
  async getAgentsByCategory(): Promise<Record<string, AgentConfig[]>> {
    // Fetch all agents and group by category field from DB
    const allAgents = await this.listAgents();
    const grouped: Record<string, AgentConfig[]> = {};

    for (const agent of allAgents) {
      const category = agent.category || 'other';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(agent);
    }

    return grouped;
  },

  // Get agent by ID
  async getAgent(agentId: string): Promise<AgentConfig> {
    const response = await axiosClient.get(`/agents/${agentId}`);
    // Backend now returns AgentConfig directly - no transformation needed
    return response.data;
  },

  // Create agent
  async createAgent(agent: Partial<AgentConfig>): Promise<AgentConfig> {
    const payload = toCreateAgentRequest(agent);
    const response = await axiosClient.post('/agents', payload);
    return response.data;
  },

  // Update agent
  async updateAgent(agentId: string, agent: Partial<AgentConfig>): Promise<AgentConfig> {
    const payload = toUpdateAgentRequest(agent);
    const response = await axiosClient.put(`/agents/${agentId}`, payload);
    return response.data;
  },

  // Delete agent
  async deleteAgent(agentId: string): Promise<void> {
    await axiosClient.delete(`/agents/${agentId}`);
  },

  // Reload agents from disk
  async reloadAgents(): Promise<{ message: string; count: number; agents: Array<{ id: string; name: string }> }> {
    const response = await axiosClient.post('/agents/reload');
    return response.data;
  },

  // Get model providers
  async getModelProviders(): Promise<ModelProviderInfo[]> {
    const response = await axiosClient.get('/agents/models/providers');
    return response.data;
  },

  // Get Raiqon models
  async getRaiqonModels(): Promise<ModelInfo[]> {
    const response = await axiosClient.get('/agents/models/all?provider=raiqon');
    return response.data;
  },

  // Get Azure AI Foundry models
  async getAzureAIFoundryModels(): Promise<ModelInfo[]> {
    const response = await axiosClient.get('/agents/models/all?provider=azure_ai_foundry');
    return response.data;
  },

  // Get context field types
  async getContextFieldTypes(): Promise<{categories: ContextFieldTypeInfo[]}> {
    const response = await axiosClient.get('/agents/context-field-types');
    return response.data;
  },

  // Get output formats
  async getOutputFormats(): Promise<OutputFormatInfo[]> {
    const response = await axiosClient.get('/agents/output-formats');
    return response.data;
  },

  // Get example classification agent
  async getExampleClassification(): Promise<AgentConfig> {
    const response = await axiosClient.get('/agents/examples/classification');
    return response.data;
  },

  // Get example review agent
  async getExampleReview(): Promise<AgentConfig> {
    const response = await axiosClient.get('/agents/examples/review');
    return response.data;
  },

  // Get example GPT-5 agent
  async getExampleGPT5(): Promise<AgentConfig> {
    const response = await axiosClient.get('/agents/examples/gpt5');
    return response.data;
  },

  // Create example agents
  async createExampleAgents(): Promise<{ message: string; agents: Array<{ id: string; name: string; category: string }> }> {
    const response = await axiosClient.post('/agents/examples/create');
    return response.data;
  },

  // Test a default agent through the real production dispatch path (POST /agents/{id}/test-live).
  // No prompt is rendered - default agents have no prompt_config. Poll the returned
  // execution_id via getExecutionStatus(), then fetch the result via getTestLiveResult().
  async testAgentLive(
    agentId: string,
    params: { item_id: string; related_item_id?: string }
  ): Promise<{ execution_id: string; task_type: string; result_id: string | null }> {
    const response = await axiosClient.post(`/agents/${agentId}/test-live`, params);
    return response.data;
  },

  // Fetch a live-test's result once its execution status is "completed".
  async getTestLiveResult(taskType: string, resultId: string): Promise<{ result: any }> {
    const response = await axiosClient.get('/agents/test-live/result', {
      params: { task_type: taskType, result_id: resultId },
    });
    return response.data;
  },

  // Run the agent builder's unsaved editor state (POST /agents/testing/draft/stream). Returns the raw
  // NDJSON response - same event stream as the saved-agent sandbox endpoints.
  async streamDraftAgentTest(
    agent: Partial<AgentConfig>,
    context: Record<string, string>,
    itemId: string | undefined,
    signal?: AbortSignal,
  ): Promise<Response> {
    const { model_slug, config } = toAgentConfigFields(agent);
    const response = await fetch(`${API_BASE_URL}/agents/testing/draft/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-API-Key': getApiKey() },
      body: JSON.stringify({
        ...(agent.id ? { agent_id: agent.id } : {}),
        name: agent.name || '',
        model_slug,
        config,
        context,
        ...(itemId ? { item_id: itemId } : {}),
      }),
      signal,
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const detail = errorData.detail;
      throw new Error(typeof detail === 'string' ? detail : detail ? JSON.stringify(detail) : `Run failed (${response.status})`);
    }
    return response;
  },

  // Stream agent test (real-time streaming)
  streamAgentTest(agentId: string, context: Record<string, any>): EventSource {
    // Use POST but with EventSource we need to use GET, so we'll use fetch with SSE
    // Actually, we'll return a custom stream reader
    return this.createStreamConnection('/agents/test/stream', { agent_id: agentId, context });
  },

  // Stream agent chat (real-time streaming)
  streamAgentChat(agentId: string, messages: Array<{role: string; content: string}>, context: Record<string, any>): EventSource {
    return this.createStreamConnection('/agents/chat/stream', {
      agent_id: agentId,
      messages,
      context
    });
  },

  // Helper to create streaming connection
  createStreamConnection(_endpoint: string, data: any): any {
    // This is a placeholder - we'll implement proper SSE/fetch streaming
    // For now, return a mock that we'll implement in the component
    return { endpoint: _endpoint, data };
  },
};

// Export combined API client
export const apiClient = {
  ...pipelineApi,
  ...agentApi,
};

export default pipelineApi;
